import styled from "styled-components";
import { space, color, layout, typography, flexbox, variant, position } from "styled-system";
import propTypes from "@styled-system/prop-types";
import { themeGet } from "@styled-system/theme-get";
import { darken } from "polished";

import { Box } from "../Grid";

const Button = styled(Box)`
  position: relative;
  display: block;
  padding-left: ${props => (props.icon ? "68px" : "43px")};
  padding-right: 43px;
  text-align: center;
  text-decoration: none;
  border: 0;
  text-transform: uppercase;
  transition: background-color ${themeGet("times.transition")};
  font-family: ${themeGet("fonts.workSans")};
  cursor: pointer;
  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.8;
  }
  ${space};
  ${color};
  ${layout};
  ${typography};
  ${flexbox};
  ${position};
  ${props =>
    variant({
      variants: {
        primary: {
          backgroundColor: "cta",
          color: "white.100",
          "&:hover": {
            backgroundColor: darken(0.1, props.theme.colors.cta),
          },
          "&:active, &:focus": {
            color: "white.100",
          },
        },
        secondary: {
          backgroundColor: "linkSecondary",
          color: "white.100",
          "&:hover": {
            backgroundColor: darken(0.1, props.theme.colors.linkSecondary),
          },
          "&:active, &:focus": {
            color: "white.100",
          },
        },
        outline: {
          backgroundColor: "transparent",
          border: "1px solid",
          borderColor: "linkSecondary",
          color: "linkSecondary",
          "&:hover": {
            backgroundColor: darken(0.1, props.theme.colors.white[100]),
          },
          "&:active, &:focus": {
            color: "linkSecondary",
          },
        },
        empty: {
          backgroundColor: "transparent",
          padding: 0,
          margin: 0,
        },
        closeModal: {
          padding: 12,
          height: 40,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "transparent",
            span: {
              color: darken(0.1, props.theme.colors.linkSecondary),
            },
            svg: {
              color: darken(0.1, props.theme.colors.linkSecondary),
            },
          },
        },
        gold: {
          backgroundColor: props.theme.colors.gold.border,
          color: props.theme.colors.white[100],
          "&:hover": {
            backgroundColor: darken(0.1, props.theme.colors.gold.border),
          },
        },
        goldOutline: {
          paddingTop: 15,
          paddingBottom: 15,
          position: "relative",
          boxShadow: `inset 0 0 0 2px ${props.theme.colors.gold.border}`,
          borderColor: props.theme.colors.gold.border,
          backgroundColor: "transparent",
          color: props.theme.colors.gold.border,
          svg: {
            position: "absolute",
            top: "50%",
            right: "17px",
            transform: "translateY(-50%)",
          },
          "&:hover": {
            boxShadow: `inset 0 0 0 2px ${darken(0.1, props.theme.colors.gold.border)}`,
            color: darken(0.1, props.theme.colors.gold.border),
            svg: {
              color: darken(0.1, props.theme.colors.gold.border),
            },
          },
        },
	      asLink: {
		      padding: 0,
		      position: "relative",
		      boxShadow: `none`,
          textTransform: "none",
		      borderColor: "transparent",
          fontSize: 2,
	        textDecoration: "underline",
		      backgroundColor: "transparent",
          fontWeight: 400,
		      color: props.theme.colors.link,
		      "&:hover": {
			      textDecoration: "none"
		      },
	      },
      },
    })}
`;

Button.displayName = "Button";

Button.defaultProps = {
  as: "button",
  variant: "primary",
  fontSize: 0,
  fontWeight: "semiBold",
  py: "15px",
};

Button.propTypes = {
  ...propTypes.space,
  ...propTypes.color,
  ...propTypes.layout,
  ...propTypes.typography,
  ...propTypes.flexbox,
  ...propTypes.position,
};

export { Button };
